import { ReactNode, useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';

import { useTheme, IconButton, useMediaQuery } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | 'xs';

interface Props {
  open: boolean;
  onClose: () => void | Promise<void | boolean> | undefined;
  title: string | JSX.Element;
  confirmText?: string;
  confirmColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  onConfirm: () => void | Promise<void | boolean> | undefined;
  cancelText?: string;
  focusOnCancel?: boolean;
  disableBackdropClick?: boolean;
  children: ReactNode;
  dividers?: boolean;
  hideCancel?: boolean;
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullScreen?: boolean;
  fullScreenBreakpoint?: Breakpoint;
  scroll?: 'body' | 'paper';
}

export interface DialogTitleProps {
  id: string;
  children?: ReactNode;
  onClose: () => void;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <MdClose />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

export default function ModalDialog(props: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(props.fullScreenBreakpoint ?? 'sm'));
  const [open, setOpen] = useState(props.open);

  useEffect(() => {
    if (props.open) setOpen(true);
  }, [props.open]);

  const handleClose = () => {
    setOpen(false);
    props.onClose();
  };

  const handleConfirm = async () => {
    const response = await props.onConfirm();

    if (response) {
      setOpen(false);
    }
  };

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (
          (reason === 'backdropClick' && !props.disableBackdropClick) ||
          !props.disableBackdropClick
        ) {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={open}
      fullWidth={props.fullWidth}
      maxWidth={props.maxWidth}
      fullScreen={props.fullScreenBreakpoint ? fullScreen : props.fullScreen}
      scroll={props.scroll}
    >
      <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
        {props.title}
      </BootstrapDialogTitle>
      <DialogContent dividers={props.dividers}>{props.children}</DialogContent>
      <DialogActions>
        {!props.hideCancel ? (
          <Button
            onClick={handleClose}
            color="inherit"
            autoFocus={props.scroll === 'body' ? false : props.focusOnCancel}
          >
            {props.cancelText ?? 'Cancelar'}
          </Button>
        ) : null}
        <Button
          onClick={handleConfirm}
          color={props.confirmColor || 'primary'}
          autoFocus={props.scroll === 'body' ? false : !props.focusOnCancel}
        >
          {props.confirmText ?? 'Confirmar'}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}
